import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { Cell, CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import React, { useCallback, useState } from 'react';
import { isSamePosition } from '@/shared/ui/data/EditableGrid/lib/position';
import { Column } from '@/shared/ui/data/EditableGrid/types/column.types';

interface IOptions {
  onSave?: (position: Position, newValue: CellValue) => void;
  onCancel?: (position: Position) => void;
  onStartEdit?: (position: Position) => void;
}

export function useEditCell(options: IOptions = {}) {
  const [editingCell, setEditingCell] = useState<Position | null>(null);
  const [tempValue, setTempValue] = useState<CellValue | null>(null);

  const startEdit = useCallback(
    (position: Position, initialValue: CellValue) => {
      setEditingCell(position);
      setTempValue(initialValue);
      options.onStartEdit?.(position);
    },
    [options]
  );

  const saveEdit = useCallback(() => {
    if (editingCell) {
      options.onSave?.(editingCell, tempValue);
      setEditingCell(null);
      setTempValue(null);
    }
  }, [editingCell, tempValue, options]);

  const cancelEdit = useCallback(() => {
    if (editingCell) {
      options.onCancel?.(editingCell);
      setEditingCell(null);
      setTempValue(null);
    }
  }, [editingCell, options]);

  const updateTempValue = useCallback((newValue: CellValue) => {
    setTempValue(newValue);
  }, []);

  const isEditing = useCallback(
    (position: Position): boolean => {
      return editingCell != null && isSamePosition(editingCell, position);
    },
    [editingCell]
  );

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      position: Position,
      columns: readonly Column[],
      cells: readonly Cell[][]
    ) => {
      if (isEditing(position)) {
        if (e.key === 'Enter') {
          e.preventDefault();
          saveEdit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancelEdit();
        }
        return;
      }

      const isStartEditKey = e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey;
      if (isStartEditKey) {
        e.preventDefault();
        const column = columns[position.colIndex];
        if (column?.editable !== false) {
          const cell = cells[position.rowIndex][position.colIndex];
          if (cell) startEdit(position, cell.value);
        }
        return;
      }
    },
    [isEditing, saveEdit, cancelEdit, startEdit]
  );

  return {
    editingCell,
    tempValue,
    startEdit,
    saveEdit,
    cancelEdit,
    updateTempValue,
    isEditing,
    handleKeyDown,
  };
}
